import {
  collection,
  doc,
  serverTimestamp,
  type CollectionReference,
  type DocumentData,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { ZodError } from 'zod'
import type { FirestoreUserProfileFavoriteCatalog } from './schema'
import { schema } from './schema'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import type { StreamInterface } from 'tricklejs/dist/types'
import { modelListStream } from '../../firestore-mobx/stream'
import { UserProfileFavoriteCatalog } from '../../models/UserProfileFavoriteCatalog'
import { deleteDocWithError, setDocWithError } from '../../firestore-mobx/fetch'

export * from './schema'

const converter: FirestoreDataConverter<FirestoreUserProfileFavoriteCatalog> = {
  toFirestore: (data) => data,
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    try {
      return schema.parse(data)
    } catch (e) {
      if (e instanceof ZodError) {
        console.error('Error parsing favorite catalog', e.errors)
      }
      throw e
    }
  },
}

const getColRef = (
  firestore: Firestore,
  userId: string
): CollectionReference<FirestoreUserProfileFavoriteCatalog, DocumentData> => {
  return collection(
    firestore,
    'user_profile',
    userId,
    'favorite_catalog'
  ).withConverter(converter)
}

export const getUserProfileFavoriteCatalogs = (
  repository: FirebaseRepository,
  params: { userId: string }
): StreamInterface<UserProfileFavoriteCatalog[]> => {
  const ref = getColRef(repository.firestore, params.userId)

  return modelListStream(repository, ref, UserProfileFavoriteCatalog)
}

export const addUserProfileFavoriteCatalog = async (
  repository: FirebaseRepository,
  params: {
    userId: string
    catalogId: string
  }
): Promise<void> => {
  const ref = getColRef(repository.firestore, params.userId)
  const docRef = doc(ref, params.catalogId)

  await setDocWithError(docRef, {
    userId: params.userId,
    updatedAt: serverTimestamp(),
  })
}

export const removeUserProfileFavoriteCatalog = async (
  repository: FirebaseRepository,
  params: {
    userId: string
    catalogId: string
  }
): Promise<void> => {
  const ref = doc(
    getColRef(repository.firestore, params.userId),
    params.catalogId
  )

  return deleteDocWithError(ref)
}
